class KoyoTheme {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.backdrop = document.querySelector('[data-cart-backdrop]');
    this.lastFocused = null;
    this.bindGlobalEvents();
    this.init(document);
  }

  init(root) {
    root.querySelectorAll('[data-home-hero]').forEach((hero) => this.initHomeHero(hero));
    root.querySelectorAll('.moisturiser-choice').forEach((section) => this.initMoisturiserChoice(section));
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => this.initGallery(gallery));
    root.querySelectorAll('[data-product-form]').forEach((form) => this.initProductForm(form));
    root.querySelectorAll('[data-sticky-product]').forEach((sticky) => this.initStickyProduct(sticky));
    root.querySelectorAll('[data-formula]').forEach((formula) => this.initFormula(formula));
    root.querySelectorAll('[data-product-routine]').forEach((routine) => this.initProductRoutine(routine));
    root.querySelectorAll('[data-product-card-gallery]').forEach((card) => this.initProductCardGallery(card));
    root.querySelectorAll('[data-routine-step]').forEach((step) => this.initRoutineStep(step));
    root.querySelectorAll('[data-principles]').forEach((story) => this.initPrinciples(story));
    root.querySelectorAll('[data-lazy-video]').forEach((video) => this.initLazyVideo(video));
    root.querySelectorAll('[data-reveal]').forEach((element) => this.observeReveal(element));
  }

  initLazyVideo(video) {
    if (video.dataset.lazyVideoInitialized) return;
    video.dataset.lazyVideoInitialized = 'true';
    const sources = [...video.querySelectorAll('source[data-src]')];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldAutoplay = video.hasAttribute('data-autoplay') && !reduced;
    let hydrated = sources.length === 0;

    const hydrate = () => {
      if (hydrated) return;
      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      hydrated = true;
      video.load();
    };

    video.addEventListener('pointerdown', hydrate, { once: true });
    video.addEventListener('keydown', hydrate, { once: true });

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        hydrate();
        if (shouldAutoplay) video.play().catch(() => {});
      } else if (shouldAutoplay) {
        video.pause();
      }
    }, { rootMargin: '320px 0px', threshold: 0.01 });

    observer.observe(video);
  }

  initHomeHero(hero) {
    if (hero.dataset.initialized) return;
    hero.dataset.initialized = 'true';
    const slides = [...hero.querySelectorAll('[data-home-hero-slide]')];
    const dots = [...hero.querySelectorAll('[data-home-hero-dot]')];
    const status = hero.querySelector('[data-home-hero-status]');
    if (slides.length < 2 || dots.length !== slides.length) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canAutoplay = hero.dataset.autoplay === 'true' && !reduced && window.matchMedia('(min-width: 50.01rem)').matches;
    const interval = Math.max(7000, Number(hero.dataset.interval) || 9000);
    let index = 0;
    let timer = null;
    let userPaused = false;

    const stop = () => {
      if (timer) window.clearTimeout(timer);
      timer = null;
      slides.forEach((slide) => slide.querySelector('video')?.pause());
    };
    const schedule = () => {
      stop();
      if (!canAutoplay || userPaused || document.hidden) return;
      timer = window.setTimeout(() => show(index + 1, false), interval);
    };
    const show = (nextIndex, announce = true) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        const video = slide.querySelector('video');
        if (video) active && !reduced ? video.play().catch(() => {}) : video.pause();
      });
      dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === index)));
      if (announce && status) status.textContent = `Campaign ${index + 1} of ${slides.length}`;
      schedule();
    };
    const pauseForUser = () => { userPaused = true; stop(); };

    hero.querySelector('[data-home-hero-prev]')?.addEventListener('click', () => { pauseForUser(); show(index - 1); });
    hero.querySelector('[data-home-hero-next]')?.addEventListener('click', () => { pauseForUser(); show(index + 1); });
    dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => { pauseForUser(); show(dotIndex); }));
    hero.addEventListener('pointerenter', stop);
    hero.addEventListener('pointerleave', schedule);
    hero.addEventListener('focusin', stop);
    hero.addEventListener('touchstart', pauseForUser, { passive: true, once: true });
    document.addEventListener('visibilitychange', schedule);
    show(0, false);
  }

  initMoisturiserChoice(section) {
    if (section.dataset.initialized) return;
    section.dataset.initialized = 'true';
    const tabs = [...section.querySelectorAll('[data-moisturiser-tab]')];
    const panels = [...section.querySelectorAll('[data-moisturiser-panel]')];
    if (tabs.length !== 2 || panels.length !== 2) return;
    const mobile = window.matchMedia('(max-width: 50rem)');
    let activeSide = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.side || tabs[0].dataset.side;
    const sync = () => {
      tabs.forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.side === activeSide)));
      panels.forEach((panel) => {
        panel.hidden = mobile.matches && panel.dataset.side !== activeSide;
      });
    };
    tabs.forEach((tab) => tab.addEventListener('click', () => { activeSide = tab.dataset.side; sync(); }));
    mobile.addEventListener('change', sync);
    sync();
  }

  bindGlobalEvents() {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-cart-open]');
      if (opener) {
        event.preventDefault();
        this.openCart(opener);
      }
      if (event.target.closest('[data-cart-close]') || event.target === this.backdrop) this.closeCart();
      const cartChange = event.target.closest('[data-cart-change]');
      if (cartChange) {
        event.preventDefault();
        this.updateCartLine(cartChange.dataset.cartKey, Number(cartChange.dataset.cartQuantity), cartChange).catch(() => {});
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer?.getAttribute('aria-hidden') === 'false') this.closeCart();
    });
    document.addEventListener('shopify:section:load', (event) => this.init(event.target));
  }

  initGallery(gallery) {
    if (gallery.dataset.initialized) return;
    gallery.dataset.initialized = 'true';
    const stage = gallery.querySelector('[data-gallery-main]');
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const templates = [...gallery.querySelectorAll('[data-gallery-template]')];
    const indexLabel = gallery.querySelector('[data-gallery-index]');
    if (!stage || !thumbs.length || templates.length !== thumbs.length) return;
    let index = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
    const show = (nextIndex) => {
      index = (nextIndex + thumbs.length) % thumbs.length;
      stage.classList.add('is-changing');
      window.setTimeout(() => {
        const template = templates[index];
        stage.replaceChildren(template.content.cloneNode(true));
        stage.classList.remove('is-changing');
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 140);
      thumbs.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      if (indexLabel) indexLabel.textContent = String(index + 1);
    };
    thumbs.forEach((thumb, thumbIndex) => thumb.addEventListener('click', () => show(thumbIndex)));
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => show(index - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(index + 1));
  }

  initProductForm(form) {
    if (form.dataset.initialized) return;
    form.dataset.initialized = 'true';
    const quantity = form.querySelector('input[name="quantity"]');
    const submit = form.querySelector('[data-add-to-cart]');
    const error = form.querySelector('[data-product-form-error]');
    form.querySelector('[data-quantity-minus]')?.addEventListener('click', () => quantity?.stepDown());
    form.querySelector('[data-quantity-plus]')?.addEventListener('click', () => quantity?.stepUp());
    if (form.dataset.nativeSubmit === 'true') return;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!submit || submit.disabled) return;
      const label = submit.querySelector('span');
      const original = label?.textContent || 'Add to cart';
      submit.disabled = true;
      submit.classList.add('is-loading');
      if (error) error.hidden = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'This item could not be added.');
        if (label) label.textContent = 'Added ✓';
        await this.refreshCart();
        this.openCart(submit);
        window.setTimeout(() => { if (label) label.textContent = original; }, 1100);
      } catch (formError) {
        if (error) {
          error.textContent = formError.message;
          error.hidden = false;
        }
      } finally {
        submit.disabled = false;
        submit.classList.remove('is-loading');
      }
    });
  }

  initStickyProduct(sticky) {
    if (sticky.dataset.initialized) return;
    sticky.dataset.initialized = 'true';
    const form = document.getElementById(sticky.dataset.formId);
    const mainButton = form?.querySelector('[data-add-to-cart]');
    const stickyButton = sticky.querySelector('button');
    if (!form || !mainButton || !stickyButton) return;
    new IntersectionObserver(([entry]) => {
      sticky.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > 280);
    }).observe(mainButton);
    stickyButton.addEventListener('click', () => form.requestSubmit());
  }

  initFormula(formula) {
    if (formula.dataset.initialized) return;
    formula.dataset.initialized = 'true';
    const chapters = [...formula.querySelectorAll('[data-formula-chapter]')];
    const value = formula.querySelector('[data-formula-value]');
    const label = formula.querySelector('[data-formula-label]');
    const heading = formula.querySelector('[data-formula-heading]');
    const progress = formula.querySelector('[data-formula-progress]');
    const stage = formula.querySelector('.formula-stage');
    if (!chapters.length) return;
    const activate = (chapter) => {
      chapters.forEach((item) => {
        const active = item === chapter;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      if (value) value.textContent = chapter.dataset.value || '';
      if (label) label.textContent = chapter.dataset.label || '';
      if (heading) heading.textContent = chapter.dataset.heading || '';
      if (progress) progress.textContent = chapter.dataset.index || '';
      if (stage) stage.dataset.state = chapter.dataset.index || '1';
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) activate(entry.target); });
    }, { rootMargin: '-30% 0px -42%', threshold: 0.18 });
    chapters.forEach((chapter) => {
      observer.observe(chapter);
      chapter.addEventListener('click', () => activate(chapter));
      chapter.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const current = chapters.indexOf(chapter);
        const next = event.key === 'ArrowRight' ? current + 1 : current - 1;
        const target = chapters[(next + chapters.length) % chapters.length];
        activate(target);
        target.focus();
      });
    });
    activate(chapters[0]);
  }

  initProductRoutine(routine) {
    if (routine.dataset.initialized) return;
    routine.dataset.initialized = 'true';
    const rail = routine.querySelector('[data-routine-rail]');
    const cards = [...routine.querySelectorAll('[data-routine-card]')];
    const indicators = [...routine.querySelectorAll('[data-routine-progress]')];
    if (!rail || !cards.length || indicators.length !== cards.length) return;
    const setActive = (index) => indicators.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
    const update = () => {
      const railLeft = rail.getBoundingClientRect().left;
      let closest = 0;
      let distance = Infinity;
      cards.forEach((card, index) => {
        const current = Math.abs(card.getBoundingClientRect().left - railLeft);
        if (current < distance) { distance = current; closest = index; }
      });
      setActive(closest);
    };
    rail.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    setActive(0);
  }

  initProductCardGallery(card) {
    if (card.dataset.initialized) return;
    card.dataset.initialized = 'true';
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const media = card.querySelector('.product-card__media');
    const image = card.querySelector('[data-card-main]');
    const sources = [...card.querySelectorAll('[data-card-images] [data-src]')];
    const bars = [...card.querySelectorAll('.product-card__progress i')];
    if (!media || !image || sources.length < 2) return;
    let current = 0;
    let transitionToken = 0;
    const show = (index) => {
      if (index === current) return;
      current = index;
      const token = ++transitionToken;
      const source = sources[index];
      const nextImage = new Image();
      nextImage.addEventListener('load', () => {
        if (token !== transitionToken) return;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reduced) image.classList.add('is-changing');
        window.setTimeout(() => {
          if (token !== transitionToken) return;
          image.src = source.dataset.src;
          image.srcset = '';
          image.alt = source.dataset.alt || '';
          image.classList.remove('is-changing');
          if (!reduced) image.animate([{ opacity: 0.45, transform: 'scale(1.012)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 320, easing: 'cubic-bezier(.22,1,.36,1)' });
        }, reduced ? 0 : 110);
      }, { once: true });
      nextImage.src = source.dataset.src;
      nextImage.decode?.().catch(() => {});
      bars.forEach((bar, barIndex) => bar.classList.toggle('is-active', barIndex === index));
    };
    media.addEventListener('pointermove', (event) => {
      const rect = media.getBoundingClientRect();
      show(Math.min(sources.length - 1, Math.floor(((event.clientX - rect.left) / rect.width) * sources.length)));
    });
    media.addEventListener('pointerleave', () => show(0));
    media.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); show((current + 1) % sources.length); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); show((current - 1 + sources.length) % sources.length); }
    });
  }

  initRoutineStep(step) {
    if (step.dataset.initialized) return;
    step.dataset.initialized = 'true';
    const image = step.querySelector('.home-routine__image');
    const name = step.querySelector('[data-routine-name]');
    const link = step.querySelector('[data-routine-link]');
    const choices = [...step.querySelectorAll('[data-routine-choice]')];
    choices.forEach((choice) => choice.addEventListener('click', () => {
      choices.forEach((item) => {
        const active = item === choice;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      image?.classList.add('is-changing');
      window.setTimeout(() => {
        if (image && choice.dataset.image) { image.src = choice.dataset.image; image.srcset = ''; image.alt = choice.dataset.name || ''; }
        image?.classList.remove('is-changing');
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 140);
      if (name) name.textContent = choice.dataset.name || '';
      if (link && choice.dataset.url) link.href = choice.dataset.url;
    }));
  }

  initPrinciples(story) {
    if (story.dataset.initialized) return;
    story.dataset.initialized = 'true';
    const image = story.querySelector('.principles-story__image');
    const label = story.querySelector('[data-principles-label]');
    const number = story.querySelector('[data-principles-number]');
    const chapters = [...story.querySelectorAll('[data-principles-chapter]')];
    chapters.forEach((chapter) => chapter.addEventListener('click', () => {
      chapters.forEach((item) => {
        const active = item === chapter;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      image?.classList.add('is-changing');
      window.setTimeout(() => {
        if (image?.tagName === 'IMG' && chapter.dataset.image) { image.src = chapter.dataset.image; image.srcset = ''; image.alt = chapter.dataset.label || ''; }
        image?.classList.remove('is-changing');
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 160);
      if (label) label.textContent = chapter.dataset.label || '';
      if (number) number.textContent = chapter.dataset.number || '';
    }));
  }

  observeReveal(element) {
    if (element.dataset.revealReady) return;
    element.dataset.revealReady = 'true';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      element.classList.add('is-visible');
      return;
    }
    new IntersectionObserver(([entry], observer) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    }, { rootMargin: '0px 0px -8%' }).observe(element);
  }

  async refreshCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    this.renderCart(cart);
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
      count.hidden = cart.item_count === 0;
    });
  }

  renderCart(cart) {
    const items = this.drawer?.querySelector('[data-cart-items]');
    const footer = this.drawer?.querySelector('[data-cart-footer]');
    const total = this.drawer?.querySelector('[data-cart-total]');
    if (!items || !footer) return;
    items.replaceChildren();
    if (!cart.items.length) {
      const empty = document.createElement('p');
      empty.textContent = 'Your cart is empty.';
      items.append(empty);
      footer.hidden = true;
      return;
    }
    cart.items.forEach((item) => {
      const article = document.createElement('article');
      article.className = 'cart-drawer__item';
      if (item.image) {
        const mediaLink = document.createElement('a');
        mediaLink.href = item.url;
        const image = document.createElement('img');
        image.src = item.image; image.alt = item.product_title; image.width = 88; image.height = 88;
        mediaLink.append(image); article.append(mediaLink);
      }
      const copy = document.createElement('div');
      copy.className = 'cart-drawer__item-copy';
      const title = document.createElement('a'); title.href = item.url; title.textContent = item.product_title;
      copy.append(title);
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const variant = document.createElement('span'); variant.className = 'cart-drawer__meta'; variant.textContent = item.variant_title; copy.append(variant);
      }
      Object.entries(item.properties || {}).forEach(([propertyName, propertyValue]) => {
        if (!propertyValue || propertyName.startsWith('_')) return;
        const property = document.createElement('span'); property.className = 'cart-drawer__property'; property.textContent = `${propertyName}: ${propertyValue}`; copy.append(property);
      });
      const price = document.createElement('span'); price.className = 'cart-drawer__price'; price.textContent = this.formatMoney(item.final_line_price); copy.append(price);
      const controls = document.createElement('div'); controls.className = 'cart-drawer__controls'; controls.setAttribute('aria-label', `Quantity for ${item.product_title}`);
      const minus = document.createElement('button'); minus.type = 'button'; minus.textContent = '−'; minus.setAttribute('aria-label', `Decrease ${item.product_title} quantity`); minus.dataset.cartChange = ''; minus.dataset.cartKey = item.key; minus.dataset.cartQuantity = String(Math.max(0, item.quantity - 1));
      const quantity = document.createElement('span'); quantity.textContent = String(item.quantity); quantity.setAttribute('aria-live', 'polite');
      const plus = document.createElement('button'); plus.type = 'button'; plus.textContent = '+'; plus.setAttribute('aria-label', `Increase ${item.product_title} quantity`); plus.dataset.cartChange = ''; plus.dataset.cartKey = item.key; plus.dataset.cartQuantity = String(item.quantity + 1);
      const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = 'Remove'; remove.className = 'cart-drawer__remove'; remove.setAttribute('aria-label', `Remove ${item.product_title} from cart`); remove.dataset.cartChange = ''; remove.dataset.cartKey = item.key; remove.dataset.cartQuantity = '0';
      controls.append(minus, quantity, plus, remove); copy.append(controls); article.append(copy); items.append(article);
    });
    if (total) total.textContent = this.formatMoney(cart.total_price);
    footer.hidden = false;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 0 }).format(cents / 100);
  }

  async updateCartLine(key, quantity, trigger) {
    if (!key || !Number.isFinite(quantity)) return;
    trigger.disabled = true;
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
    });
    const cart = await response.json();
    if (!response.ok) {
      trigger.disabled = false;
      return;
    }
    this.renderCart(cart);
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
      count.hidden = cart.item_count === 0;
    });
  }

  openCart(trigger) {
    if (!this.drawer || !this.backdrop) return;
    this.lastFocused = trigger || document.activeElement;
    this.drawer.removeAttribute('inert');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.backdrop.hidden = false;
    document.body.classList.add('drawer-open');
    this.drawer.focus();
    this.refreshCart().catch(() => {});
  }

  closeCart() {
    if (!this.drawer || !this.backdrop) return;
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');
    this.backdrop.hidden = true;
    document.body.classList.remove('drawer-open');
    this.lastFocused?.focus?.();
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => new KoyoTheme());
else new KoyoTheme();
